import {
  CloseOutlineIcon,
  type IconProps,
  NoticeIcon,
} from '@carboncopyco/icons';
import classNames from 'classnames';
import type React from 'react';
import { ColorHex, ColorNames } from '../../constants';
import { Spacer } from '../Spacer';
import { TextBase, TextButton } from '../Typography';

export type BannerProps = {
  onClose: () => void;
  content?: React.ReactNode;
  secondary?: boolean;
  danger?: boolean;
  accent2?: boolean;
  dark?: boolean;
  label: string;
  containedWidth?: boolean;
  icon?: React.ReactElement<IconProps>;
} & (
  | {
      actionLabel?: never;
      onActionClick?: never;
    }
  | {
      actionLabel: string;
      onActionClick: () => void;
    }
);

export const Banner = ({
  actionLabel,
  onActionClick,
  onClose,
  content,
  secondary,
  danger,
  accent2,
  dark,
  label,
  containedWidth,
  icon,
}: BannerProps) => {
  return (
    <div
      className={classNames(
        [
          'h-10 flex justify-end items-center border-none',
          secondary
            ? 'bg-ds-secondary-100'
            : danger
              ? 'bg-ds-danger-100'
              : accent2
                ? 'bg-ds-accent2-100'
                : dark
                  ? 'bg-ds-natural-600'
                  : 'bg-ds-primary-100',
        ],
        { 'w-screen': !containedWidth, 'w-full': containedWidth }
      )}
    >
      <div className="flex w-full items-center justify-center">
        {content ? (
          content
        ) : (
          <>
            {icon ?? (
              <NoticeIcon
                size={20}
                color={
                  secondary
                    ? ColorHex.secondary500
                    : danger
                      ? ColorHex.danger500
                      : accent2
                        ? ColorHex.accent2500
                        : dark
                          ? ColorHex.accent3100
                          : ColorHex.primary500
                }
              />
            )}
            <Spacer amount={0.4} />
            <TextBase
              color={
                secondary
                  ? ColorNames.secondary500
                  : danger
                    ? ColorNames.danger500
                    : accent2
                      ? ColorNames.accent2500
                      : dark
                        ? ColorNames.accent3100
                        : ColorNames.primary500
              }
              label={label}
            />
            {onActionClick && (
              <>
                <Spacer amount={1.25} />
                <div
                  className={classNames([
                    'cursor-pointer underline',
                    secondary
                      ? 'text-ds-secondary-500'
                      : danger
                        ? 'text-ds-danger-500'
                        : accent2
                          ? 'text-ds-accent2-500'
                          : dark
                            ? 'text-ds-accent3-100'
                            : 'text-ds-primary-500',
                  ])}
                  onClick={onActionClick}
                >
                  <TextButton
                    color={
                      secondary
                        ? 'text-ds-secondary-500'
                        : danger
                          ? 'text-ds-danger-500'
                          : accent2
                            ? 'text-ds-accent2-500'
                            : dark
                              ? 'text-ds-accent3-100'
                              : 'text-ds-primary-500'
                    }
                    label={actionLabel}
                  />
                </div>
              </>
            )}
          </>
        )}
      </div>
      <div
        onClick={onClose}
        className="w-24 cursor-pointer flex items-center justify-end"
      >
        <CloseOutlineIcon
          color={
            secondary
              ? ColorHex.secondary500
              : danger
                ? ColorHex.danger500
                : accent2
                  ? ColorHex.accent2500
                  : dark
                    ? ColorHex.accent3100
                    : ColorHex.primary500
          }
        />
        <Spacer amount={2.5} />
      </div>
    </div>
  );
};
